import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Tenant } from "../schemas/tenantSchema";
import {
  TenantFilters,
  tenantService,
  TenantsResponse,
} from "../api/services/tenantService";

export const useTenants = (
  filters: TenantFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: tenants,
    isLoading: isTenantsLoading,
    refetch,
  } = useQuery<TenantsResponse, Error>({
    queryKey: ["tenants", filters],
    queryFn: () => tenantService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetTenantById = (id?: string) =>
    useQuery<Tenant, Error>({
      queryKey: ["tenant", id],
      queryFn: () => tenantService.getById(id!),
      enabled: !!id,
    });

  const createTenantMutation = useMutation({
    mutationFn: tenantService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create tenant");
    },
  });

  const updateTenantMutation = useMutation({
    mutationFn: tenantService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update tenant");
    },
  });

  const deleteTenantMutation = useMutation({
    mutationFn: tenantService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Tenant deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete tenant");
    },
  });

  return {
    tenants,
    isLoading:
      isTenantsLoading ||
      createTenantMutation.isPending ||
      updateTenantMutation.isPending ||
      deleteTenantMutation.isPending,
    useGetTenantById,
    refetchTenants: refetch,
    createTenant: createTenantMutation.mutate,
    updateTenant: updateTenantMutation.mutate,
    deleteTenants: deleteTenantMutation.mutate,
  };
};
